'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

// Vertical thin gold rail on the right edge of the viewport with a small dot
// that moves down as the page scrolls. A subtle scroll-position indicator
// that doubles as a "you are here" marker. Renders only on long pages
// (document height > 1.5 viewports) and only on the public site.
export function ScrollProgressRail() {
  const pathname = usePathname();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const rafRef = useRef<number | null>(null);
  const ticking = useRef(false);

  useEffect(() => {
    if (pathname?.startsWith('/admin')) {
      setVisible(false);
      return;
    }
    function compute() {
      const docH = document.documentElement.scrollHeight;
      const viewH = window.innerHeight;
      if (docH < viewH * 1.5) {
        setVisible(false);
        return;
      }
      setVisible(true);
      const scrolled = window.scrollY;
      const p = Math.max(0, Math.min(1, scrolled / (docH - viewH)));
      setProgress(p);
    }
    compute();
    function onScroll() {
      if (ticking.current) return;
      ticking.current = true;
      rafRef.current = requestAnimationFrame(() => {
        compute();
        ticking.current = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', compute);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [pathname]);

  if (!visible) return null;

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        right: 14,
        top: '20vh',
        bottom: '20vh',
        width: 1,
        background: 'color-mix(in srgb, var(--accent) 18%, transparent)',
        zIndex: 5,
        pointerEvents: 'none',
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: -3,
          top: `${(progress * 100).toFixed(2)}%`,
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: 'var(--accent)',
          boxShadow: '0 0 12px color-mix(in srgb, var(--accent) 60%, transparent)',
          transform: 'translateY(-50%)',
          transition: 'top 240ms var(--ease-calm)',
        }}
      />
    </div>
  );
}
